import type { DbExecutor } from "./executor";
import type { ProposedCostRepository } from "./repositories/proposedCostRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { RoomRepository } from "./repositories/roomRepository";
import type { DesignRepository } from "./repositories/designRepository";
import type { SettingsRepository } from "./repositories/settingsRepository";
import { ValidationError } from "./repositories/projectRepository";
import { generateId } from "./ids";
import {
  budgetControl,
  compareToBaseline,
  type BaselineComparison,
  type BudgetControl,
  type ChangeFindingLine,
  type CurrentLine,
} from "../engine/baselineCompare";
import {
  formatChangeOrderNumber,
  validateBaselineInput,
  validateChangeOrderInput,
  type Baseline,
  type BaselineLine,
  type ChangeOrder,
  type ChangeOrderLine,
  type ChangeOrderStatus,
} from "../domain/baseline";
import type { CostType } from "../domain/proposedCostLine";
import type { Cents } from "../domain/money";

/**
 * Baselines & Change Orders (Phase 11, spec §27).
 *
 * WORKING ESTIMATES MAY CHANGE; BASELINES DO NOT.
 *
 * Creating a baseline freezes a normalized snapshot of the approved scope —
 * line by line, with the quantity, product and package assumptions that
 * produced each number. Nothing afterwards can move it: not editing a
 * feature, not changing the labor rate, not a new offer, not new historical
 * evidence, not switching the Active design. Later changes are explained
 * against it, and authorized (or not) through Change Orders.
 *
 * This service reads planning records and writes only baseline/change-order
 * records. It never touches actual accounting: approving a change order
 * authorizes an expectation, not a transaction (spec §35 of the brief).
 */

export interface BudgetSummary extends BudgetControl {
  baseline: Baseline | null;
  changeOrders: ChangeOrder[];
  /** The design the baseline approved — NOT necessarily the active one. */
  approvedDesignId: string | null;
  approvedDesignName: string;
  activeDesignId: string | null;
  activeDesignName: string;
  /** True when the user is working in a design other than the approved one. */
  workingOffApprovedDesign: boolean;
}

export class BaselineService {
  constructor(
    private readonly db: DbExecutor,
    private readonly proposedCosts: ProposedCostRepository,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly rooms: RoomRepository,
    private readonly designs: DesignRepository,
    private readonly settings: SettingsRepository,
  ) {}

  /* ------------------------------------------------------------------ */
  /* Reading                                                             */
  /* ------------------------------------------------------------------ */

  /** The live approved baseline (superseded ones stay readable history). */
  async currentBaseline(projectId: string): Promise<Baseline | null> {
    const rows = await this.db.select<BaselineRow>(
      "SELECT * FROM baselines WHERE project_id = ? AND status = 'approved' ORDER BY sequence DESC LIMIT 1",
      [projectId],
    );
    if (rows.length === 0) return null;
    return this.hydrateBaseline(rows[0]);
  }

  async listBaselines(projectId: string): Promise<Baseline[]> {
    const rows = await this.db.select<BaselineRow>(
      "SELECT * FROM baselines WHERE project_id = ? ORDER BY sequence",
      [projectId],
    );
    const out: Baseline[] = [];
    for (const row of rows) out.push(await this.hydrateBaseline(row));
    return out;
  }

  private async hydrateBaseline(row: BaselineRow): Promise<Baseline> {
    const lines = await this.db.select<BaselineLineRow>(
      "SELECT * FROM baseline_lines WHERE baseline_id = ? ORDER BY sort_order, rowid",
      [row.id],
    );
    return {
      id: row.id,
      projectId: row.project_id,
      sequence: Number(row.sequence),
      name: row.name,
      status: row.status === "superseded" ? "superseded" : "approved",
      designId: row.design_id,
      designName: row.design_name ?? "",
      contingencyCents: Number(row.contingency_cents ?? 0),
      estimatingTaxPctX100: row.estimating_tax_pct_x100 === null ? null : Number(row.estimating_tax_pct_x100),
      laborRateCentsPerHour: Number(row.labor_rate_cents_per_hour ?? 2500),
      purchasePriceCents: row.purchase_price_cents === null ? null : Number(row.purchase_price_cents),
      expectedArvCents: row.expected_arv_cents === null ? null : Number(row.expected_arv_cents),
      scopeCents: Number(row.scope_cents ?? 0),
      totalCents: Number(row.total_cents ?? 0),
      notes: row.notes ?? "",
      approvedBy: row.approved_by,
      approvedAt: row.approved_at,
      supersededAt: row.superseded_at,
      supersededReason: row.superseded_reason ?? "",
      createdAt: row.created_at,
      lines: lines.map(toBaselineLine),
    };
  }

  async listChangeOrders(projectId: string): Promise<ChangeOrder[]> {
    const rows = await this.db.select<ChangeOrderRow>(
      "SELECT * FROM change_orders WHERE project_id = ? ORDER BY sequence",
      [projectId],
    );
    const out: ChangeOrder[] = [];
    for (const row of rows) {
      const lines = await this.db.select<ChangeOrderLineRow>(
        "SELECT * FROM change_order_lines WHERE change_order_id = ? ORDER BY sort_order, rowid",
        [row.id],
      );
      out.push(toChangeOrder(row, lines));
    }
    return out;
  }

  /**
   * The four numbers, in one place: baseline, authorized, current working,
   * and the unapproved difference — plus which design each belongs to.
   */
  async budget(projectId: string): Promise<BudgetSummary> {
    const [baseline, changeOrders, project] = await Promise.all([
      this.currentBaseline(projectId),
      this.listChangeOrders(projectId),
      this.projectRow(projectId),
    ]);
    const activeDesignId = project.active_design_id;
    const currentLines = await this.proposedCosts.listForDesign(projectId, activeDesignId);
    const currentScopeCents = currentLines
      .filter((l) => l.archivedAt === null)
      .reduce((sum, l) => sum + l.amountCents, 0);

    const control = budgetControl({
      baseline,
      changeOrders,
      currentScopeCents,
      currentContingencyCents: Number(project.contingency_cents ?? 0),
    });
    const designNames = new Map((await this.designs.listByProject(projectId)).map((d) => [d.id, d.name]));
    return {
      ...control,
      baseline,
      changeOrders,
      approvedDesignId: project.approved_design_id,
      approvedDesignName: project.approved_design_id ? (designNames.get(project.approved_design_id) ?? "") : "",
      activeDesignId,
      activeDesignName: activeDesignId ? (designNames.get(activeDesignId) ?? "") : "",
      workingOffApprovedDesign:
        baseline !== null && project.approved_design_id !== null && project.approved_design_id !== activeDesignId,
    };
  }

  private async projectRow(projectId: string): Promise<ProjectRow> {
    const rows = await this.db.select<ProjectRow>(
      `SELECT id, contingency_cents, estimating_tax_pct_x100, purchase_price_cents, expected_arv_cents,
              active_design_id, approved_design_id
         FROM projects WHERE id = ?`,
      [projectId],
    );
    if (rows.length === 0) throw new ValidationError(["That project no longer exists."]);
    return rows[0];
  }

  /* ------------------------------------------------------------------ */
  /* Creating the baseline                                               */
  /* ------------------------------------------------------------------ */

  /** The live estimate reduced to comparable lines (also used for preview). */
  async currentLines(projectId: string, designId: string | null): Promise<CurrentLine[]> {
    const [lines, features, rooms, instances] = await Promise.all([
      this.proposedCosts.listForDesign(projectId, designId),
      this.features.listByProject(projectId),
      this.rooms.listByProject(projectId),
      this.assemblies.listByProject(projectId),
    ]);
    const featureById = new Map(features.map((f) => [f.id, f]));
    const roomById = new Map(rooms.map((r) => [r.id, r]));
    const componentIndex = new Map<string, { unitCostCents: Cents; quantityX100: number | null; unit: string; assemblyName: string }>();
    for (const instance of instances) {
      for (const component of instance.components) {
        componentIndex.set(component.id, {
          unitCostCents: component.unitCostCents,
          quantityX100: component.qtyOverrideX100,
          unit: component.unit,
          assemblyName: instance.name,
        });
      }
    }
    const selections = await this.db.select<{ component_id: string; product_id: string; product_name_snapshot: string }>(
      `SELECT s.component_id, s.product_id, s.product_name_snapshot
         FROM project_product_selections s
         JOIN assembly_instance_components c ON c.id = s.component_id
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN project_features f ON f.id = i.project_feature_id
        WHERE f.project_id = ? AND s.replaced_at IS NULL`,
      [projectId],
    );
    const selectionByComponent = new Map(selections.map((s) => [s.component_id, s]));
    const packageByFeature = new Map(
      (
        await this.db.select<{ id: string; package_application_id: string | null }>(
          "SELECT id, package_application_id FROM project_features WHERE project_id = ?",
          [projectId],
        )
      ).map((r) => [r.id, r.package_application_id]),
    );

    return lines
      .filter((line) => line.archivedAt === null)
      .map((line) => {
        const feature = line.featureId ? featureById.get(line.featureId) : undefined;
        const room = feature?.roomId ? roomById.get(feature.roomId) : undefined;
        const component = line.assemblyComponentId ? componentIndex.get(line.assemblyComponentId) : undefined;
        const selection = line.assemblyComponentId ? selectionByComponent.get(line.assemblyComponentId) : undefined;
        const effectiveQuantity = feature
          ? (feature.quantityX100 ?? feature.quantityGeometryX100 ?? null)
          : null;
        return {
          id: line.id,
          featureId: line.featureId,
          featureName: feature?.name ?? "",
          roomName: room?.name ?? "",
          categoryId: line.categoryId,
          costType: line.costType,
          description: line.description,
          amountCents: line.amountCents,
          quantityX100: component?.quantityX100 ?? effectiveQuantity,
          quantityUnit: component?.unit ?? feature?.quantityUnit ?? null,
          unitCostCents: component?.unitCostCents ?? null,
          productId: selection?.product_id ?? null,
          productName: selection?.product_name_snapshot ?? "",
          assemblyComponentId: line.assemblyComponentId,
          packageApplicationId: line.featureId ? (packageByFeature.get(line.featureId) ?? null) : null,
        };
      });
  }

  /**
   * Freeze the current approved expectation.
   *
   * Also records the APPROVED DESIGN: from here on, working in another
   * proposal moves only the Active design.
   */
  async createBaseline(
    projectId: string,
    input: { name?: string; notes?: string; approvedBy?: string | null } = {},
  ): Promise<Baseline> {
    const name = (input.name ?? "Initial Baseline").trim();
    const problems = validateBaselineInput({ name });
    if (problems.length > 0) throw new ValidationError(problems);
    const existing = await this.currentBaseline(projectId);
    if (existing) {
      throw new ValidationError([
        `This project already has an approved baseline (${existing.name}). Authorize changes with a change order, or supersede the baseline explicitly.`,
      ]);
    }
    return this.freeze(projectId, { name, notes: input.notes ?? "", approvedBy: input.approvedBy ?? null, sequence: 1 });
  }

  /**
   * ADMINISTRATIVE CORRECTION (spec §27): an erroneous baseline is never
   * edited — it is superseded, and both remain readable. Approved change
   * orders stay attached to the baseline they were approved against.
   */
  async supersedeBaseline(
    projectId: string,
    input: { reason: string; name?: string; approvedBy?: string | null },
  ): Promise<Baseline> {
    if (input.reason.trim() === "") {
      throw new ValidationError(["Superseding a baseline needs a reason — it is a correction, not an edit."]);
    }
    const existing = await this.currentBaseline(projectId);
    if (!existing) throw new ValidationError(["This project has no baseline to supersede."]);
    const now = new Date().toISOString();
    let created: Baseline | null = null;
    await this.db.transaction(async () => {
      await this.db.execute(
        "UPDATE baselines SET status = 'superseded', superseded_at = ?, superseded_reason = ?, updated_at = ? WHERE id = ?",
        [now, input.reason.trim(), now, existing.id],
      );
      created = await this.freeze(projectId, {
        name: (input.name ?? `Corrected Baseline ${existing.sequence + 1}`).trim(),
        notes: `Supersedes ${existing.name}: ${input.reason.trim()}`,
        approvedBy: input.approvedBy ?? null,
        sequence: existing.sequence + 1,
      });
    });
    return created!;
  }

  private async freeze(
    projectId: string,
    input: { name: string; notes: string; approvedBy: string | null; sequence: number },
  ): Promise<Baseline> {
    const project = await this.projectRow(projectId);
    const designId = project.active_design_id;
    const designs = await this.designs.listByProject(projectId);
    const designName = designId ? (designs.find((d) => d.id === designId)?.name ?? "") : "";
    const lines = await this.currentLines(projectId, designId);
    const laborRate = await this.settings.getLaborRateCentsPerHour();
    const scopeCents = lines.reduce((sum, l) => sum + l.amountCents, 0);
    const contingencyCents = Number(project.contingency_cents ?? 0);
    const id = generateId();
    const now = new Date().toISOString();

    await this.db.transaction(async () => {
      await this.db.execute(
        `INSERT INTO baselines (id, project_id, sequence, name, status, design_id, design_name,
           contingency_cents, estimating_tax_pct_x100, labor_rate_cents_per_hour, purchase_price_cents,
           expected_arv_cents, scope_cents, total_cents, notes, approved_by, approved_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, 'approved', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id, projectId, input.sequence, input.name, designId, designName,
          contingencyCents, project.estimating_tax_pct_x100, laborRate, project.purchase_price_cents,
          project.expected_arv_cents, scopeCents, scopeCents + contingencyCents, input.notes,
          input.approvedBy, now, now, now,
        ],
      );
      // Package provenance is carried through for future analytics (§36).
      const packageNames = new Map(
        (
          await this.db.select<{ id: string; package_name_snapshot: string }>(
            "SELECT id, package_name_snapshot FROM package_applications WHERE project_id = ?",
            [projectId],
          )
        ).map((r) => [r.id, r.package_name_snapshot]),
      );
      const featureRooms = new Map(
        (
          await this.db.select<{ id: string; room_id: string | null; design_id: string | null }>(
            "SELECT id, room_id, design_id FROM project_features WHERE project_id = ?",
            [projectId],
          )
        ).map((r) => [r.id, r]),
      );
      let order = 10;
      for (const line of lines) {
        const feature = line.featureId ? featureRooms.get(line.featureId) : undefined;
        await this.db.execute(
          `INSERT INTO baseline_lines (id, baseline_id, source_line_id, feature_id, feature_name, room_id, room_name,
             design_id, category_id, cost_type, description, amount_cents, quantity_x100, quantity_unit,
             unit_cost_cents, product_id, product_name, assembly_component_id, assembly_name,
             package_application_id, package_name, sort_order)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            generateId(), id, line.id, line.featureId, line.featureName, feature?.room_id ?? null, line.roomName,
            feature?.design_id ?? null, line.categoryId, line.costType, line.description, line.amountCents,
            line.quantityX100, line.quantityUnit, line.unitCostCents, line.productId, line.productName,
            line.assemblyComponentId, "", line.packageApplicationId,
            line.packageApplicationId ? (packageNames.get(line.packageApplicationId) ?? "") : "",
            order,
          ],
        );
        order += 10;
      }
      // Approving a baseline sets the APPROVED design — the only thing that
      // ever does (switching the Active design never touches it).
      if (designId) {
        await this.db.execute("UPDATE projects SET approved_design_id = ?, updated_at = ? WHERE id = ?", [
          designId, now, projectId,
        ]);
      }
    });

    return (await this.hydrateBaseline(
      (await this.db.select<BaselineRow>("SELECT * FROM baselines WHERE id = ?", [id]))[0],
    ));
  }

  /* ------------------------------------------------------------------ */
  /* Comparison                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * What has changed since approval, in domain terms — with the findings
   * already authorized by an approved change order marked, so the same
   * change is never authorized twice.
   */
  async unapprovedChanges(projectId: string, designId?: string | null): Promise<BaselineComparison & { baseline: Baseline | null }> {
    const baseline = await this.currentBaseline(projectId);
    if (!baseline) {
      return { baseline: null, findings: [], addedCents: 0, removedCents: 0, changedCents: 0, netDeltaCents: 0, authorizedKeys: [] };
    }
    const project = await this.projectRow(projectId);
    const evaluated = designId === undefined ? project.active_design_id : designId;
    const current = await this.currentLines(projectId, evaluated);
    const authorized = await this.db.select<{ finding_key: string }>(
      `SELECT l.finding_key FROM change_order_lines l
         JOIN change_orders o ON o.id = l.change_order_id
        WHERE o.project_id = ? AND o.status = 'approved' AND l.finding_key <> ''`,
      [projectId],
    );
    const comparison = compareToBaseline(baseline, current, {
      authorizedKeys: new Set(authorized.map((a) => a.finding_key)),
    });
    return { ...comparison, baseline };
  }

  /* ------------------------------------------------------------------ */
  /* Change orders                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Create a DRAFT change order from selected findings (and/or manual
   * allowance lines). A draft authorizes nothing until it is approved.
   */
  async createChangeOrder(
    projectId: string,
    input: {
      title: string;
      reason?: string;
      notes?: string;
      findings?: ChangeFindingLine[];
      manualLines?: { description: string; deltaCents: Cents; categoryId?: string | null; costType?: CostType | null; notes?: string }[];
      createdBy?: string | null;
      /** 'budget' raises the authorized budget; 'contingency' draws on the
       *  frozen contingency instead (spec §27). Split = 'contingency' plus a
       *  smaller contingencyCents than the net delta. */
      fundingSource?: "budget" | "contingency";
      contingencyCents?: Cents;
    },
  ): Promise<ChangeOrder> {
    const problems = validateChangeOrderInput({ title: input.title });
    if (problems.length > 0) throw new ValidationError(problems);
    const baseline = await this.currentBaseline(projectId);
    if (!baseline) {
      throw new ValidationError(["Create the project's baseline first — a change order changes something already approved."]);
    }
    const findings = input.findings ?? [];
    const manualLines = input.manualLines ?? [];
    if (findings.length === 0 && manualLines.length === 0) {
      throw new ValidationError(["Select at least one change (or add a manual line) for this change order."]);
    }
    const project = await this.projectRow(projectId);
    const designs = await this.designs.listByProject(projectId);
    const id = generateId();
    const now = new Date().toISOString();
    let number = "";
    await this.db.transaction(async () => {
      const sequence = await this.nextSequence(projectId);
      number = formatChangeOrderNumber(sequence);
      const net =
        findings.reduce((sum, f) => sum + f.deltaCents, 0) +
        manualLines.reduce((sum, l) => sum + l.deltaCents, 0);
      await this.db.execute(
        `INSERT INTO change_orders (id, project_id, baseline_id, sequence, number, title, status, design_id,
           design_name, net_delta_cents, reason, notes, created_by, funding_source, contingency_cents,
           created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, 'draft', ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          id, projectId, baseline.id, sequence, number, input.title.trim(), project.active_design_id,
          project.active_design_id ? (designs.find((d) => d.id === project.active_design_id)?.name ?? "") : "",
          net, input.reason ?? "", input.notes ?? "", input.createdBy ?? null,
          input.fundingSource ?? "budget",
          input.fundingSource === "contingency" ? (input.contingencyCents ?? net) : (input.contingencyCents ?? 0),
          now, now,
        ],
      );
      let order = 10;
      for (const finding of findings) {
        await this.db.execute(
          `INSERT INTO change_order_lines (id, change_order_id, kind, description, feature_id, feature_name,
             room_name, category_id, cost_type, baseline_amount_cents, current_amount_cents, delta_cents,
             quantity_delta_x100, quantity_unit, product_id, product_name, baseline_line_id, source_line_id,
             finding_key, notes, sort_order)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            generateId(), id, kindFor(finding), finding.description, finding.featureId, finding.featureName,
            finding.roomName, finding.categoryId, finding.costType, finding.baselineAmountCents,
            finding.currentAmountCents, finding.deltaCents, finding.quantityDeltaX100, finding.quantityUnit,
            finding.productId, finding.productName, finding.baselineLineId, finding.sourceLineId,
            finding.key, finding.explanation, order,
          ],
        );
        order += 10;
      }
      for (const line of manualLines) {
        await this.db.execute(
          `INSERT INTO change_order_lines (id, change_order_id, kind, description, category_id, cost_type,
             baseline_amount_cents, current_amount_cents, delta_cents, notes, sort_order)
           VALUES (?, ?, 'manual', ?, ?, ?, 0, ?, ?, ?, ?)`,
          [
            generateId(), id, line.description, line.categoryId ?? null, line.costType ?? null,
            line.deltaCents, line.deltaCents, line.notes ?? "", order,
          ],
        );
        order += 10;
      }
    });
    return (await this.getChangeOrder(id))!;
  }

  /** Per-project numbering, allocated in-transaction; never MAX()+1. */
  private async nextSequence(projectId: string): Promise<number> {
    await this.db.execute(
      "INSERT OR IGNORE INTO change_order_sequences (project_id, next_seq) VALUES (?, 1)",
      [projectId],
    );
    const rows = await this.db.select<{ next_seq: number }>(
      "SELECT next_seq FROM change_order_sequences WHERE project_id = ?",
      [projectId],
    );
    const sequence = Number(rows[0]?.next_seq ?? 1);
    await this.db.execute("UPDATE change_order_sequences SET next_seq = ? WHERE project_id = ?", [
      sequence + 1,
      projectId,
    ]);
    return sequence;
  }

  async getChangeOrder(id: string): Promise<ChangeOrder | null> {
    const rows = await this.db.select<ChangeOrderRow>("SELECT * FROM change_orders WHERE id = ?", [id]);
    if (rows.length === 0) return null;
    const lines = await this.db.select<ChangeOrderLineRow>(
      "SELECT * FROM change_order_lines WHERE change_order_id = ? ORDER BY sort_order, rowid",
      [id],
    );
    return toChangeOrder(rows[0], lines);
  }

  /** Approving contributes the change order's net delta to the budget. */
  async approveChangeOrder(id: string, approvedBy: string | null = null): Promise<void> {
    const order = await this.getChangeOrder(id);
    if (!order) throw new ValidationError(["That change order no longer exists."]);
    if (order.status === "approved") return;
    if (order.status === "cancelled") {
      throw new ValidationError(["A cancelled change order is history — create a new one instead."]);
    }
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE change_orders SET status = 'approved', approved_at = ?, approved_by = ?, updated_at = ? WHERE id = ?",
      [now, approvedBy, now, id],
    );
  }

  /** Cancelled orders remain history — they are never deleted. */
  async cancelChangeOrder(id: string, reason = ""): Promise<void> {
    const order = await this.getChangeOrder(id);
    if (!order) throw new ValidationError(["That change order no longer exists."]);
    if (order.status === "approved") {
      throw new ValidationError([
        "An approved change order is part of the authorized budget. Reverse it with a new change order rather than cancelling history.",
      ]);
    }
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE change_orders SET status = 'cancelled', cancelled_at = ?, notes = ?, updated_at = ? WHERE id = ?",
      [now, reason || order.notes, now, id],
    );
  }

  /** Editing a DRAFT is ordinary work; approved orders are never edited. */
  async updateDraft(id: string, input: { title?: string; reason?: string; notes?: string }): Promise<void> {
    const order = await this.getChangeOrder(id);
    if (!order) throw new ValidationError(["That change order no longer exists."]);
    if (order.status !== "draft") {
      throw new ValidationError(["Only a draft change order can be edited."]);
    }
    await this.db.execute(
      "UPDATE change_orders SET title = ?, reason = ?, notes = ?, updated_at = ? WHERE id = ?",
      [(input.title ?? order.title).trim(), input.reason ?? order.reason, input.notes ?? order.notes, new Date().toISOString(), id],
    );
  }

  /**
   * Approve a different design as the project's plan (spec §27 / brief §21).
   *
   * The baseline is NOT replaced: the delta against the current authorized
   * budget is authorized through a change order, the newly approved design
   * becomes the Approved design, and every earlier design and the original
   * baseline stay exactly as they were.
   */
  async approveDesign(
    projectId: string,
    designId: string,
    input: { title?: string; reason?: string; approvedBy?: string | null } = {},
  ): Promise<{ changeOrder: ChangeOrder | null; approvedDesignId: string }> {
    const design = await this.designs.getById(designId);
    if (!design || design.projectId !== projectId) {
      throw new ValidationError(["That design does not belong to this project."]);
    }
    if (design.kind === "existing") {
      throw new ValidationError(["The Existing design documents the house as it is — it is never the approved plan."]);
    }
    const baseline = await this.currentBaseline(projectId);
    if (!baseline) {
      throw new ValidationError(["Create the project's baseline first."]);
    }
    const comparison = await this.unapprovedChanges(projectId, designId);
    let changeOrder: ChangeOrder | null = null;
    if (comparison.findings.length > 0) {
      changeOrder = await this.createChangeOrder(projectId, {
        title: input.title ?? `Approve design: ${design.name}`,
        reason: input.reason ?? `Authorizes the difference between the approved plan and ${design.name}.`,
        findings: comparison.findings,
        createdBy: input.approvedBy ?? null,
      });
      await this.approveChangeOrder(changeOrder.id, input.approvedBy ?? null);
      changeOrder = await this.getChangeOrder(changeOrder.id);
    }
    await this.db.execute("UPDATE projects SET approved_design_id = ?, updated_at = ? WHERE id = ?", [
      designId,
      new Date().toISOString(),
      projectId,
    ]);
    return { changeOrder, approvedDesignId: designId };
  }
}

function kindFor(finding: ChangeFindingLine): "added" | "removed" | "changed" {
  if (finding.type === "added") return "added";
  if (finding.type === "removed") return "removed";
  return "changed";
}

interface ProjectRow {
  id: string;
  contingency_cents: number | null;
  estimating_tax_pct_x100: number | null;
  purchase_price_cents: number | null;
  expected_arv_cents: number | null;
  active_design_id: string | null;
  approved_design_id: string | null;
}

interface BaselineRow {
  id: string;
  project_id: string;
  sequence: number;
  name: string;
  status: string;
  design_id: string | null;
  design_name: string | null;
  contingency_cents: number | null;
  estimating_tax_pct_x100: number | null;
  labor_rate_cents_per_hour: number | null;
  purchase_price_cents: number | null;
  expected_arv_cents: number | null;
  scope_cents: number | null;
  total_cents: number | null;
  notes: string | null;
  approved_by: string | null;
  approved_at: string;
  superseded_at: string | null;
  superseded_reason: string | null;
  created_at: string;
}

interface BaselineLineRow {
  id: string;
  baseline_id: string;
  source_line_id: string | null;
  feature_id: string | null;
  feature_name: string;
  room_id: string | null;
  room_name: string;
  design_id: string | null;
  category_id: string | null;
  cost_type: string | null;
  description: string;
  amount_cents: number;
  quantity_x100: number | null;
  quantity_unit: string | null;
  unit_cost_cents: number | null;
  product_id: string | null;
  product_name: string;
  assembly_component_id: string | null;
  assembly_name: string;
  package_application_id: string | null;
  package_name: string;
  sort_order: number;
}

function toBaselineLine(row: BaselineLineRow): BaselineLine {
  return {
    id: row.id,
    baselineId: row.baseline_id,
    sourceLineId: row.source_line_id,
    featureId: row.feature_id,
    featureName: row.feature_name ?? "",
    roomId: row.room_id,
    roomName: row.room_name ?? "",
    designId: row.design_id,
    categoryId: row.category_id,
    costType: (row.cost_type as CostType | null) ?? null,
    description: row.description ?? "",
    amountCents: Number(row.amount_cents ?? 0),
    quantityX100: row.quantity_x100 === null ? null : Number(row.quantity_x100),
    quantityUnit: row.quantity_unit,
    unitCostCents: row.unit_cost_cents === null ? null : Number(row.unit_cost_cents),
    productId: row.product_id,
    productName: row.product_name ?? "",
    assemblyComponentId: row.assembly_component_id,
    assemblyName: row.assembly_name ?? "",
    packageApplicationId: row.package_application_id,
    packageName: row.package_name ?? "",
    sortOrder: Number(row.sort_order ?? 0),
  };
}

interface ChangeOrderRow {
  id: string;
  funding_source?: string | null;
  contingency_cents?: number | null;
  project_id: string;
  baseline_id: string;
  sequence: number;
  number: string;
  title: string;
  status: string;
  design_id: string | null;
  design_name: string | null;
  net_delta_cents: number;
  reason: string | null;
  notes: string | null;
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  cancelled_at: string | null;
  created_at: string;
}

interface ChangeOrderLineRow {
  id: string;
  change_order_id: string;
  kind: string;
  description: string;
  feature_id: string | null;
  feature_name: string;
  room_name: string;
  category_id: string | null;
  cost_type: string | null;
  baseline_amount_cents: number;
  current_amount_cents: number;
  delta_cents: number;
  quantity_delta_x100: number | null;
  quantity_unit: string | null;
  product_id: string | null;
  product_name: string;
  baseline_line_id: string | null;
  source_line_id: string | null;
  finding_key: string;
  notes: string;
  sort_order: number;
}

function toChangeOrder(row: ChangeOrderRow, lines: ChangeOrderLineRow[]): ChangeOrder {
  return {
    id: row.id,
    projectId: row.project_id,
    baselineId: row.baseline_id,
    number: row.number,
    title: row.title,
    status: row.status as ChangeOrderStatus,
    designId: row.design_id,
    designName: row.design_name ?? "",
    netDeltaCents: Number(row.net_delta_cents ?? 0),
    fundingSource: (row.funding_source as "budget" | "contingency") ?? "budget",
    contingencyCents: Number(row.contingency_cents ?? 0),
    reason: row.reason ?? "",
    notes: row.notes ?? "",
    createdBy: row.created_by,
    approvedBy: row.approved_by,
    createdAt: row.created_at,
    approvedAt: row.approved_at,
    cancelledAt: row.cancelled_at,
    lines: lines.map(
      (l): ChangeOrderLine => ({
        id: l.id,
        changeOrderId: l.change_order_id,
        kind: (l.kind as ChangeOrderLine["kind"]) ?? "changed",
        description: l.description ?? "",
        featureId: l.feature_id,
        featureName: l.feature_name ?? "",
        roomName: l.room_name ?? "",
        categoryId: l.category_id,
        costType: (l.cost_type as CostType | null) ?? null,
        baselineAmountCents: Number(l.baseline_amount_cents ?? 0),
        currentAmountCents: Number(l.current_amount_cents ?? 0),
        deltaCents: Number(l.delta_cents ?? 0),
        quantityDeltaX100: l.quantity_delta_x100 === null ? null : Number(l.quantity_delta_x100),
        quantityUnit: l.quantity_unit,
        productId: l.product_id,
        productName: l.product_name ?? "",
        baselineLineId: l.baseline_line_id,
        sourceLineId: l.source_line_id,
        notes: l.notes ?? "",
        sortOrder: Number(l.sort_order ?? 0),
      }),
    ),
  };
}
